import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
} from "@mui/material";
import { toast } from "react-hot-toast";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createDailyPunchAction,
  clearCreateDailyPunchAction,
  updateDailyPunchAction,
  clearUpdateDailyPunchAction,
} from "../../../store/actions";

const TAB_DAY_START = "start";
const TAB_DAY_END = "end";
const IS_DEV = process.env.NODE_ENV === "development";

// Resolves with { latitude, longitude } - never rejects
// In dev: returns mock coords so backend validation passes
const DEV_COORDS = { latitude: "0.000000", longitude: "0.000000" };
const getGPSCoords = () =>
  new Promise((resolve) => {
    if (IS_DEV) return resolve(DEV_COORDS);
    if (!navigator.geolocation) return resolve(DEV_COORDS);
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }),
      () => resolve(DEV_COORDS),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

const TravelPunchingLog = () => {
  const dispatch = useDispatch();

  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails"));
    } catch {
      return null;
    }
  }, []);
  const userId = userDetails?.id;

  const {
    loading: submitLoading,
    successData: submitSuccess,
    error: submitError,
    errorInfo: submitErrorInfo,
  } = useSelector((state) => state.createDailyPunchReducer || {});

  const {
    loading: updateLoading,
    successData: updateSuccess,
    error: updateError,
    errorInfo: updateErrorInfo,
  } = useSelector((state) => state.updateDailyPunchReducer || {});

  const [dayStatus, setDayStatus] = useState("WORKING");
  const isNonWorkingDay = dayStatus === "LEAVE" || dayStatus === "HOLIDAY";

  const [startData, setStartData] = useState({
    odo: "",
    photo: null,
    photoPreview: "",
    inPersonPhoto: null,
    inPersonPhotoPreview: "",
  });

  const [endData, setEndData] = useState({
    odo: "",
    photo: null,
    photoPreview: "",
    inPersonPhoto: null,
    inPersonPhotoPreview: "",
  });

  const [gpsCapturing, setGpsCapturing] = useState(false);
  const [travelId, setTravelId] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(TAB_DAY_START);

  // Read navigation state when coming from list edit
  const location = useLocation();
  const navigate = useNavigate();
  const listPath = location.pathname.includes("masterData")
    ? "/masterData/travelPunchingLogList"
    : location.pathname.includes("amrm")
    ? "/amrm/dailyActivity"
    : "/SOorMDO/travelPunchingLogList";

  useEffect(() => {
    const editRecord = location.state?.editRecord;
    if (!editRecord) return;
    setTravelId(editRecord.travelId);
    setActiveTab(TAB_DAY_END);
    setStartData((prev) => ({
      ...prev,
      odo: editRecord.odometerStart ?? prev.odo,
    }));
    setEndData((prev) => ({
      ...prev,
      odo: editRecord.odometerEnd ?? "",
    }));
    window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Camera capture + preview
  const handlePhoto = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (type === "start") {
      setStartData((p) => ({ ...p, photo: file, photoPreview: preview }));
    } else {
      setEndData((p) => ({ ...p, photo: file, photoPreview: preview }));
    }
  };

  // In-person (selfie) photo capture
  const handleInPersonPhoto = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (type === "start") {
      setStartData((p) => ({ ...p, inPersonPhoto: file, inPersonPhotoPreview: preview }));
    } else {
      setEndData((p) => ({ ...p, inPersonPhoto: file, inPersonPhotoPreview: preview }));
    }
  };

  // Start Day - Leave/Holiday closes day immediately with minimal payload
  const handleStartDay = async () => {
    const nowIso = new Date().toISOString();
    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("day_status", dayStatus);

    if (isNonWorkingDay) {
      formData.append("start_punch_time", nowIso);
      formData.append("end_punch_time", nowIso);
      formData.append("total_distance_km", "0");
      dispatch(createDailyPunchAction(formData));
      return;
    }

    setGpsCapturing(true);
    const coords = await getGPSCoords();
    setGpsCapturing(false);

    formData.append("start_latitude", coords.latitude);
    formData.append("start_longitude", coords.longitude);
    formData.append("odometer_start", startData.odo);
    formData.append("start_punch_time", nowIso);
    if (startData.photo) formData.append("start_photo_path", startData.photo);
    if (startData.inPersonPhoto) formData.append("in_person_photo", startData.inPersonPhoto);
    dispatch(createDailyPunchAction(formData));
  };

  // End Day - GPS optional, submission always proceeds
  const handleSubmit = async () => {
    if (Number(endData.odo) <= Number(startData.odo)) {
      setErrors({ odo: "End odometer must be greater than start odometer" });
      return;
    }
    setErrors({});
    setGpsCapturing(true);
    const coords = await getGPSCoords();
    setGpsCapturing(false);
    const totalKm = Math.max(0, Number(endData.odo) - Number(startData.odo));
    const formData = new FormData();
    formData.append("end_latitude", coords.latitude);
    formData.append("end_longitude", coords.longitude);
    formData.append("odometer_end", endData.odo);
    formData.append("total_distance_km", String(totalKm));
    formData.append("end_punch_time", new Date().toISOString());
    if (endData.photo) formData.append("end_photo_path", endData.photo);
    if (endData.inPersonPhoto) formData.append("in_person_photo", endData.inPersonPhoto);
    formData.endPoint = `employee-daily-travel/${travelId}`;
    dispatch(updateDailyPunchAction(formData));
  };

  // React to start day success
  useEffect(() => {
    if (submitSuccess) {
      const id = submitSuccess?.data?.id || submitSuccess?.id;
      if (id) setTravelId(id);
      if (!isNonWorkingDay) setActiveTab(TAB_DAY_END);
      dispatch(clearCreateDailyPunchAction());
      toast.dismiss();
      toast.success(isNonWorkingDay ? "Status marked successfully!" : "Day started successfully!");
      navigate(listPath);
    }
  }, [submitSuccess, dispatch, navigate, listPath, isNonWorkingDay]);

  // React to end day (update) success
  useEffect(() => {
    if (updateSuccess) {
      dispatch(clearUpdateDailyPunchAction());
      toast.dismiss();
      toast.success("Day ended successfully!");
      navigate(listPath);
    }
  }, [updateSuccess, dispatch, navigate, listPath]);

  // React to errors
  useEffect(() => {
    if (submitError) {
      toast.dismiss();
      toast.error(submitErrorInfo || "Failed to start day. Please try again.");
    }
  }, [submitError, submitErrorInfo]);

  useEffect(() => {
    if (updateError) {
      toast.dismiss();
      toast.error(updateErrorInfo || "Failed to end day. Please try again.");
    }
  }, [updateError, updateErrorInfo]);

  // Computed total distance
  const totalDistanceKm =
    endData.odo !== "" && startData.odo !== ""
      ? Math.max(0, Number(endData.odo) - Number(startData.odo))
      : null;

  const isStartLoading = gpsCapturing || submitLoading;
  const isEndLoading = gpsCapturing || updateLoading;

  return (
    <Box sx={{ p: 2, minHeight: "100vh" ,mt: { xs: 7, sm: 0 } }}>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3 }}>
            {/* Tabs at top */}
            <Box sx={{ display: "flex", mb: 2 }}>
              <Button
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  background: activeTab === TAB_DAY_START ? "#4F46E5" : "#222",
                  fontWeight: 600,
                  fontSize: 16,
                  boxShadow: "none",
                  cursor: "default",
                  "&.Mui-disabled": {
                    background: activeTab === TAB_DAY_START ? "#4F46E5" : "#222",
                    color: "#fff",
                    opacity: 1,
                  },
                }}
                disabled
              >
                Day Start
              </Button>
              <Button
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  background: activeTab === TAB_DAY_END ? "#4F46E5" : "#999999",
                  fontWeight: 600,
                  fontSize: 16,
                  boxShadow: "none",
                  cursor: "default",
                  "&.Mui-disabled": {
                    background: activeTab === TAB_DAY_END ? "#4F46E5" : "#999999",
                    color: "#fff",
                    opacity: 1,
                  },
                }}
                disabled
              >
                Day End
              </Button>
            </Box>

            <CardContent>
              {/* Day Start Tab */}
              {activeTab === TAB_DAY_START && (
                <Grid>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <FormControl>
                        <FormLabel sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}>
                          Day Status
                        </FormLabel>
                        <RadioGroup row value={dayStatus} onChange={(e) => setDayStatus(e.target.value)}>
                          <FormControlLabel value="WORKING" control={<Radio size="small" />} label="Working" />
                          <FormControlLabel value="LEAVE" control={<Radio size="small" />} label="Leave" />
                          <FormControlLabel value="HOLIDAY" control={<Radio size="small" />} label="Holiday" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {!isNonWorkingDay && (
                      <>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Start Odometer"
                            type="text"
                            inputMode="numeric"
                            value={startData.odo}
                            onChange={(e) => setStartData({ ...startData, odo: e.target.value })}
                          />
                        </Grid>

                        <Grid item size={{ xs: 12, sm: 6 }}>
                          <Button fullWidth variant="outlined" component="label" startIcon={<CameraAltIcon />} size="small">
                            Start Photo
                            <input
                              hidden
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => handlePhoto(e, "start")}
                            />
                          </Button>
                          {startData.photoPreview && (
                            <Box mt={1}>
                              <img src={startData.photoPreview} alt="start preview" width="100%" style={{ borderRadius: 8 }} />
                            </Box>
                          )}
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                          <Button fullWidth variant="outlined" component="label" startIcon={<CameraAltIcon />} size="small">
                            Selfie
                            <input
                              hidden
                              type="file"
                              accept="image/*"
                              capture="user"
                              onChange={(e) => handleInPersonPhoto(e, "start")}
                            />
                          </Button>
                          {startData.inPersonPhotoPreview && (
                            <Box mt={1}>
                              <img
                                src={startData.inPersonPhotoPreview}
                                alt="selfie preview"
                                width="100%"
                                style={{ borderRadius: 8 }}
                              />
                            </Box>
                          )}
                        </Grid>
                      </>
                    )}

                    {isNonWorkingDay && (
                      <Grid item size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">
                          Leave/Holiday selected. No odometer or photos required.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        sx={{ py: 1.2, px: 4, borderRadius: 3, minWidth: 120 }}
                      >
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleStartDay} sx={{ py: 1.2, px: 4, borderRadius: 3, minWidth: 140 }}>
                        {isStartLoading ? <CircularProgress size={24} /> : "Start Day"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* Day End Tab */}
              {activeTab === TAB_DAY_END && (
                <Grid>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="End Odometer"
                        type="text"
                        inputMode="numeric"
                        error={!!errors.odo}
                        helperText={errors.odo}
                        value={endData.odo}
                        onChange={(e) => setEndData({ ...endData, odo: e.target.value })}
                      />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Total Distance (km)"
                        value={totalDistanceKm !== null ? totalDistanceKm : ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Button fullWidth variant="outlined" component="label" startIcon={<CameraAltIcon />} size="small">
                        End Photo
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handlePhoto(e, "end")}
                        />
                      </Button>
                      {endData.photoPreview && (
                        <Box mt={1}>
                          <img src={endData.photoPreview} alt="end preview" width="100%" style={{ borderRadius: 8 }} />
                        </Box>
                      )}
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Button fullWidth variant="outlined" component="label" startIcon={<CameraAltIcon />} size="small">
                        Selfie
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          capture="user"
                          onChange={(e) => handleInPersonPhoto(e, "end")}
                        />
                      </Button>
                      {endData.inPersonPhotoPreview && (
                        <Box mt={1}>
                          <img
                            src={endData.inPersonPhotoPreview}
                            alt="selfie preview"
                            width="100%"
                            style={{ borderRadius: 8 }}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        size="large"
                        disabled={isEndLoading || !endData.odo || !endData.photo || !endData.inPersonPhoto || !travelId}
                        onClick={handleSubmit}
                        sx={{ py: 1.2, px: 4, borderRadius: 3, minWidth: 140 }}
                      >
                        {isEndLoading ? <CircularProgress size={24} /> : "End Day"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TravelPunchingLog;
